/**
 * The sidebar's tree, assembled from flat rows.
 *
 * The service hands back folders and items as lists and this builds the shape a
 * sidebar wants, which is a different thing: sorted the way somebody reads, with
 * empty folders still visible, with counts that include what is nested inside, and
 * with a folder whose parent has gone appearing at the top rather than nowhere.
 * None of that is a fact about the data, so none of it belongs in a query.
 *
 * Pure, and tested. A workspace with fifty datasets and a hundred documents is the
 * case this exists for, and "the tree looks wrong" is the hardest kind of bug to
 * chase through a component.
 */

/** What every tree node has, whichever section it is in. */
export interface TreeItem {
  id: string;
  name: string;
  folderId: string | null;
  /** For sorting by when. ISO. */
  updatedAt: string;
  /** A word or two under the name: "412 rows", "3 pages". */
  note?: string;
  /** Shown as a state rather than a detail: "Needs data", "Being produced". */
  badge?: { text: string; tone?: "good" | "ask" | "bad" };
}

export interface FolderItem {
  id: string;
  parentId: string | null;
  name: string;
}

export interface TreeFolder {
  kind: "folder";
  id: string;
  name: string;
  depth: number;
  folders: TreeFolder[];
  items: TreeItem[];
  /** Everything inside, however deeply. What a collapsed folder should say. */
  total: number;
}

export interface Tree {
  folders: TreeFolder[];
  /** Items with no folder, or whose folder has gone. */
  items: TreeItem[];
  total: number;
}

export type SortBy = "name" | "recent";

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, "en-GB", { sensitivity: "base" });
const byRecent = (a: TreeItem, b: TreeItem) => b.updatedAt.localeCompare(a.updatedAt) || byName(a, b);

/**
 * Builds one section's tree.
 *
 * `filter` is applied to items only, and a folder survives it if anything inside
 * it did. Hiding a folder whose children matched would be the more literal reading
 * and would make searching useless: the thing you are looking for is inside
 * something.
 */
export function buildTree(folders: FolderItem[], items: TreeItem[], opts: { sort?: SortBy; filter?: string } = {}): Tree {
  const sort = opts.sort ?? "name";
  const needle = (opts.filter ?? "").trim().toLowerCase();
  const matching = needle ? items.filter((i) => i.name.toLowerCase().includes(needle)) : items;

  const ids = new Set(folders.map((f) => f.id));
  const byId = new Map(folders.map((f) => [f.id, f]));

  /**
   * Whether walking up from a folder ever reaches the top.
   *
   * A folder whose parent is missing does — a missing parent *is* the top. One in
   * a cycle does not, and that is the case worth handling: the service refuses to
   * make a cycle, but a row edited by hand would otherwise give a tree where
   * neither folder is anybody's root, so `childrenOf(null)` comes back empty and
   * both vanish from the sidebar while both rows still exist. Treated as roots
   * they are visible and flattened, which is wrong-but-fixable rather than gone.
   */
  const reachesTop = (start: FolderItem): boolean => {
    const seen = new Set<string>();
    let cursor: FolderItem | undefined = start;
    while (cursor) {
      if (seen.has(cursor.id)) return false;
      seen.add(cursor.id);
      if (!cursor.parentId) return true;
      cursor = byId.get(cursor.parentId);
    }
    return true;
  };

  const childrenOf = new Map<string | null, FolderItem[]>();
  for (const folder of folders) {
    // A folder whose parent has gone is a root folder. The reference is soft on
    // purpose, and the alternative is a folder that exists and cannot be reached.
    const parent = folder.parentId && ids.has(folder.parentId) && reachesTop(folder) ? folder.parentId : null;
    childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), folder]);
  }

  const itemsOf = new Map<string | null, TreeItem[]>();
  for (const item of matching) {
    const folder = item.folderId && ids.has(item.folderId) ? item.folderId : null;
    itemsOf.set(folder, [...(itemsOf.get(folder) ?? []), item]);
  }

  const build = (folder: FolderItem, depth: number, seen: Set<string>): TreeFolder => {
    // Guarded against a cycle in the data: the service refuses to make one, and a
    // sidebar that recursed for ever over a row somebody edited by hand would take
    // the whole application with it.
    const next = new Set(seen).add(folder.id);
    const nested = (childrenOf.get(folder.id) ?? []).filter((f) => !next.has(f.id)).sort(byName).map((f) => build(f, depth + 1, next));
    const own = (itemsOf.get(folder.id) ?? []).sort(sort === "recent" ? byRecent : byName);
    return {
      kind: "folder",
      id: folder.id,
      name: folder.name,
      depth,
      folders: nested,
      items: own,
      total: own.length + nested.reduce((n, f) => n + f.total, 0)
    };
  };

  const roots = (childrenOf.get(null) ?? []).sort(byName).map((f) => build(f, 0, new Set()));
  const loose = (itemsOf.get(null) ?? []).sort(sort === "recent" ? byRecent : byName);
  return { folders: roots, items: loose, total: loose.length + roots.reduce((n, f) => n + f.total, 0) };
}

/** One folder's contents, as a list screen shows them. */
export interface FolderView {
  /** The folder being shown, or null for the top level. */
  current: TreeFolder | null;
  /** The way back up, outermost first, ending with the current folder. */
  crumbs: Array<{ id: string; name: string }>;
  folders: TreeFolder[];
  /** The things directly inside, by id, so a screen keeps its own order and rows. */
  itemIds: Set<string>;
}

/**
 * One level of the tree, for the pane beside it.
 *
 * Built from the same tree as the sidebar, so the two cannot disagree about where
 * something is: a document whose folder was deleted is at the top in both. An address
 * naming a folder that no longer exists shows the top level, because a link to a
 * folder somebody has since deleted should land on the things, not on an empty screen.
 */
export function folderView(folders: FolderItem[], items: TreeItem[], folderId: string | null): FolderView {
  const tree = buildTree(folders, items);
  const find = (list: TreeFolder[], trail: TreeFolder[]): TreeFolder[] | null => {
    for (const folder of list) {
      const path = [...trail, folder];
      if (folder.id === folderId) return path;
      const deeper = find(folder.folders, path);
      if (deeper) return deeper;
    }
    return null;
  };
  const path = folderId ? find(tree.folders, []) : null;
  const current = path ? path[path.length - 1]! : null;
  return {
    current,
    crumbs: (path ?? []).map((f) => ({ id: f.id, name: f.name })),
    folders: current ? current.folders : tree.folders,
    itemIds: new Set((current ? current.items : tree.items).map((i) => i.id))
  };
}

/**
 * Every folder a thing could be moved into, as a flat list with its depth.
 *
 * Used by the "move to" menu, which is a flat list rather than a submenu tree
 * because a submenu of submenus is unusable with a trackpad and this is at most
 * six levels of indent.
 *
 * `exclude` drops a folder and everything under it: moving a folder into its own
 * child is refused by the service, and offering it would be offering a wrong
 * answer confidently.
 */
export function moveTargets(folders: FolderItem[], exclude?: string): Array<{ id: string; name: string; depth: number }> {
  const out: Array<{ id: string; name: string; depth: number }> = [];
  const walk = (parentId: string | null, depth: number, seen: Set<string>) => {
    for (const folder of folders.filter((f) => f.parentId === parentId && !seen.has(f.id)).sort(byName)) {
      if (folder.id === exclude) continue;
      out.push({ id: folder.id, name: folder.name, depth });
      walk(folder.id, depth + 1, new Set(seen).add(folder.id));
    }
  };
  walk(null, 0, new Set());
  return out;
}
